import { isFuture, parseISO } from 'date-fns';
import { Transaction } from 'ethers';
import { useTranslation } from 'next-i18next';
import React, { useContext, useEffect, useState, VFC } from 'react';
import { useRouter } from 'next/router'

import { useCreation } from '@/apis/aniftyBackend/creations/getCreation';
import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import { useMintAsk } from '@/apis/aniftyBackend/mintAsks/getMintAsk';
import { primaryBuy as primaryBuyIpfs } from '@/apis/blockchain/AniftyMarketplaceV2Ipfs';
import {
  aniftyTokenV2GraphClient,
  marketplaceV3GraphClient,
} from '@/apis/theGraph';
import LoadingCard from '@/components/LoadingCard';
import { TransactionSucceededMessage } from '@/components/TransactionSucceededMessage';
import NOT_ON_SALE_TOKEN_IDS from '@/constants/notOnSaleTokenIDs';
import CreationSaleCard from '@/containers/creations/CreationDetail/CreationSaleCard';
import { Web3Context } from '@/contexts/Web3Provider/Web3Provider';
import { captureException } from '@/utils/Sentry';

import styles from './V2CreationSaleIpfs.module.scss';
import routes from '@/config/routes';
import redirectTargets from '@/config/redirectTargets';

type Props = {
  creation: V2CreationData;
};

const V2CreationSaleIpfs: VFC<Props> = ({ creation: creationProvided }) => {
  const router = useRouter();
  const { t } = useTranslation();

  // Refetch creation data on client side to update to latest
  const { data: creation } = useCreation({
    creationID: creationProvided.creationID,
    config: {
      initialData: creationProvided,
      refetchOnMount: 'always',
    },
  });

  const { data: mintAsk } = useMintAsk({
    askHash: creation.mintAsk.askHash,
    config: {
      refetchOnMount: 'always',
    },
  });

  const [NFTPurchaseAmt, setNFTPurchaseAmt] = useState('1');

  // form status
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [lastTransactionHash, setLastTransactionHash] =
    useState<null | string>(null);

  const web3Context = useContext(Web3Context);

  const [price, setPrice] = useState<string>(undefined);
  const [totalTokenAmount, setTotalTokenAmount] = useState<number>(undefined);
  const [availableTokenAmount, setAvailableTokenAmount] =
    useState<number>(undefined);
  const [mintAskStatus, setMintAskStatus] = useState<number>(undefined);

  useEffect(() => {
    if (!mintAsk) {
      // mint ask data is not fetched yet
      return;
    }

    // set sale info according to `mintAsk`
    setPrice(mintAsk.askOrder.price);
    creation.mintAsk.status === 3
      ? setTotalTokenAmount(0)
      : setTotalTokenAmount(mintAsk.askOrder.amount);
    // If MintAsk is cancelled, no tokens available
    creation.mintAsk.status === 3
      ? setAvailableTokenAmount(0)
      : setAvailableTokenAmount(mintAsk.availableTokenAmount);

    // if token is already minted
    if (creation.aniftyTokenID) {
      // get primary sale info from graph v3
      // to update latest price, supply, and remainingTokenAmount
      marketplaceV3GraphClient
        .getAllPrimaryListingInfoByTokenID(creation.aniftyTokenID)
        .then(({ primaryListings }) => {
          if (primaryListings && primaryListings.length) {
            let primarySale = null;

            // Case 1: Can only be one primarySale that is finished
            let sales = primaryListings.filter((ps) => ps.isCompleted === true);
            if (sales.length === 1) {
              primarySale = sales[0];

              // update price
              if (primarySale?.price) {
                setPrice(primarySale.price);
              }
              // update supply & remaining token amount
              if (
                primarySale?.totalTokenAmount &&
                primarySale?.tokenAmountSold
              ) {
                setTotalTokenAmount(Number(primarySale.totalTokenAmount));
                setAvailableTokenAmount(
                  Number(primarySale.totalTokenAmount) -
                  Number(primarySale.tokenAmountSold)
                );
                setMintAskStatus(2);
              }
            }

            // Case 2: Can only be one primarySale that is active
            if (!primarySale) {
              sales = primaryListings.filter(
                (ps) => ps.isCancelled === false && ps.isCompleted === false
              );
              if (sales.length === 1) {
                primarySale = sales[0];

                // update price
                if (primarySale?.price) {
                  setPrice(primarySale.price);
                }
                // update supply & remaining token amount
                if (
                  primarySale?.totalTokenAmount &&
                  primarySale?.tokenAmountSold
                ) {
                  setTotalTokenAmount(Number(primarySale.totalTokenAmount));
                  setAvailableTokenAmount(
                    Number(primarySale.totalTokenAmount) -
                    Number(primarySale.tokenAmountSold)
                  );
                  setMintAskStatus(1);
                }
              }
            }

            // Case 3: They are all cancelled so sort for latest primarySale
            if (!primarySale) {
              primarySale = primaryListings
                .map((ps) => ({
                  ...ps,
                  saleBlockTimestamp: Number(ps.saleBlockTimestamp),
                }))
                // get the latest one
                .sort((a, b) => b.saleBlockTimestamp - a.saleBlockTimestamp)[0];

              // update price
              if (primarySale?.price) {
                setPrice(primarySale.price);
              }
              // update supply & remaining token amount,
              // set cancelled to 0 so user cannot attempt to purchase before backend has chance to update from listener
              if (primarySale?.totalTokenAmount) {
                setTotalTokenAmount(0);
                // fetching total token amount from erc1155 graph
                // get erc1155 info from graph v2
                aniftyTokenV2GraphClient
                  .getTokenInfoByID(creation.aniftyTokenID)
                  .then(({ aniftyTokens }) => {
                    if (aniftyTokens && aniftyTokens.length === 1) {
                      setTotalTokenAmount(
                        Number(aniftyTokens[0].totalTokenAmount)
                      );
                    }
                  });
                setAvailableTokenAmount(0);
                setMintAskStatus(3);
              }
            }
          }
        });
    }
  }, [mintAsk, creation.aniftyTokenID]);

  const handleBuyButtonClick = async () => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setLastTransactionHash(null);

    if (!web3Context.web3.AniftyMarketplaceV2Ipfs) {
      // metamask is not available
      await router.push(`${routes.connectWallet}?${new URLSearchParams(redirectTargets.creationDetail(creation.creationID))}`);
    }

    try {
      const askOrder = [
        mintAsk.askOrder.askType,
        mintAsk.askOrder.signer,
        mintAsk.askOrder.currency,
        mintAsk.askOrder.amount,
        mintAsk.askOrder.price,
        mintAsk.askOrder.nonce,
        [
          mintAsk.askOrder.royalty.recipients,
          mintAsk.askOrder.royalty.royalties,
        ],
        mintAsk.askOrder.v,
        Buffer.from(mintAsk.askOrder.r, 'hex'),
        Buffer.from(mintAsk.askOrder.s, 'hex'),
      ];

      const commission = [
        mintAsk.commission.askHash,
        mintAsk.commission.amount,
        mintAsk.commission.v,
        Buffer.from(mintAsk.commission.r, 'hex'),
        Buffer.from(mintAsk.commission.s, 'hex'),
      ];
      const tokenMetadata = [
        mintAsk.commission.askHash,
        mintAsk.tokenMetadata.ipfsMetadataUri,
        mintAsk.tokenMetadata.v,
        Buffer.from(mintAsk.tokenMetadata.r, 'hex'),
        Buffer.from(mintAsk.tokenMetadata.s, 'hex'),
      ];
      const response: Transaction = await primaryBuyIpfs(
        web3Context.web3.AniftyMarketplaceV2Ipfs,
        askOrder,
        commission,
        tokenMetadata,
        NFTPurchaseAmt,
        mintAsk.askOrder.price
      );

      // notify user the transaction hash
      // idk why typing is wrong

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (response?.transactionHash) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        setLastTransactionHash(response.transactionHash);
      }
    } catch (err) {
      captureException(err);
      if (err.code === 'INSUFFICIENT_FUNDS') {
        setErrorMessage(t('creationPage.insufficientFundsError'));
      } else if (err.code === "UNSUPPORTED_OPERATION") {
        // metamask is available, but wallet is not connected
        await router.push(`${routes.connectWallet}?${new URLSearchParams(redirectTargets.creationDetail(creation.creationID))}`);
      } else {
        setErrorMessage(t('creationPage.transactionError'));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const isNotReleasedYet =
    creation.reservedAt && isFuture(parseISO(creation.reservedAt));

  return (
    <>
      {typeof price === 'undefined' ? (
        <LoadingCard message="Loading Sale Data" />
      ) : (
        <CreationSaleCard
          totalEditions={totalTokenAmount}
          availableTokenAmount={availableTokenAmount}
          ethSalePrice={price}
          purchaseAmount={parseInt(NFTPurchaseAmt)}
          onPurchaseAmountChange={setNFTPurchaseAmt}
          isCancelled={mintAskStatus === 3}
          isNotOnSale={NOT_ON_SALE_TOKEN_IDS.includes(creation.aniftyTokenID)}
          isNotReleased={isNotReleasedYet}
          isProcessing={isProcessing}
          onBuyButtonClick={handleBuyButtonClick}
        />
      )}

      <div className={styles.buyButtonContainer}>
        {errorMessage && (
          <div className={styles.errorMessage}>{errorMessage}</div>
        )}

        {lastTransactionHash && (
          <TransactionSucceededMessage transactionHash={lastTransactionHash} />
        )}
      </div>
    </>
  );
};

export default V2CreationSaleIpfs;
